import type { IssueCard } from '@tasks/contracts';

/** Чистая сортировка таблицы результатов поиска (docs/06 §3.4). */

export type SortableField = 'key' | 'summary' | 'type' | 'status' | 'assignee' | 'priority' | 'points' | 'dueDate';

export type SortState = {
  readonly columnId: string;
  readonly direction: 'asc' | 'desc';
};

const PRIORITY_WEIGHT: Readonly<Record<string, number>> = {
  highest: 5,
  high: 4,
  medium: 3,
  low: 2,
  lowest: 1,
};

function sortValue(issue: IssueCard, field: SortableField): string | number {
  switch (field) {
    case 'key':
      return issue.key;
    case 'summary':
      return issue.summary.toLowerCase();
    case 'type':
      return issue.type;
    case 'status':
      return issue.statusId;
    case 'assignee':
      return issue.assignee?.displayName.toLowerCase() ?? '';
    case 'priority':
      return PRIORITY_WEIGHT[issue.priority] ?? 0;
    case 'points':
      return issue.storyPoints ?? -1;
    case 'dueDate':
      return issue.dueDate ?? '9999-12-31';
    default:
      return issue.key;
  }
}

export function sortIssues(issues: readonly IssueCard[], sort: SortState | null): readonly IssueCard[] {
  if (!sort) {
    return issues;
  }
  const field = sort.columnId as SortableField;
  const factor = sort.direction === 'asc' ? 1 : -1;
  return [...issues].sort((left, right) => {
    const a = sortValue(left, field);
    const b = sortValue(right, field);
    if (a === b) {
      return left.key.localeCompare(right.key);
    }
    return a > b ? factor : -factor;
  });
}

export function toggleSort(current: SortState | null, columnId: string): SortState | null {
  if (!current || current.columnId !== columnId) {
    return { columnId, direction: 'asc' };
  }
  if (current.direction === 'asc') {
    return { columnId, direction: 'desc' };
  }
  return null;
}

/** Позиция ошибки JQL для подсветки в редакторе (UC-SRC-02). */
export function highlightJql(jql: string, position: number | undefined): {
  readonly before: string;
  readonly marker: string;
  readonly after: string;
} {
  if (position === undefined || position < 0 || position > jql.length) {
    return { before: jql, marker: '', after: '' };
  }
  return {
    before: jql.slice(0, position),
    marker: jql.charAt(position) || '␣',
    after: jql.slice(position + 1),
  };
}
